/**
 * The Techery Southern Highlands — proposal branding.
 *
 * Source of truth: TheTechery_BrandGuidelines_V1.pdf (Banter Group, Nov 2022).
 *
 * What lives here:
 *   - ProposalThemeTokens (colours + Poppins type stack + layout scale)
 *   - Brandmark / logo paths (served from public/branding/techery-au/logos/)
 *   - DealCategory → icon path mapping (from the 12-icon set)
 *   - Tagline + brand copy
 *
 * Why a per-tenant module: keeps Theo multi-tenant ready. When the next
 * tenant is onboarded we add a sibling file (e.g. `acme.ts`) and register
 * it in `index.ts`. Nothing in the renderer changes.
 *
 * The brand guidelines explicitly call out:
 *   - Gold (#ccaa77) is an accent only, in moderation
 *   - Dark blue (#1d2945), light grey (#eeefef), white are the primary
 *     "block" colours
 *   - Poppins SemiBold / Medium / Regular for client-facing material
 *   - "The Techery Southern Highlands" is the full tagline form
 */

import type { ProposalThemeTokens } from '@/types/proposal-premium.types';
import { DealCategory } from '@/types/theo-workflow.types';

// ────────────────────────────────────────────────────────────────────────────
// Brand colours (ex brand guide §16)
// ────────────────────────────────────────────────────────────────────────────

export const TECHERY_COLOURS = {
  darkBlue: '#1d2945',   // Pantone 533 CP — primary block
  white: '#FFFFFF',
  lightGrey: '#eeefef',  // primary block (alternating sections)
  gold: '#ccaa77',       // Pantone 466 CP — accent only, in moderation
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Asset paths — served from /public/branding/techery-au/
// ────────────────────────────────────────────────────────────────────────────

const ASSET_BASE = '/branding/techery-au';

export const TECHERY_LOGOS = {
  brandmarkOnDarkBlue: `${ASSET_BASE}/logos/brandmark-on-dark-blue.jpg`,
  brandmarkOnWhite: `${ASSET_BASE}/logos/brandmark-on-white.jpg`,
  brandmarkWhite: `${ASSET_BASE}/logos/brandmark-white.jpg`,
  brandmarkBlack: `${ASSET_BASE}/logos/brandmark-black.jpg`,
} as const;

export const TECHERY_FONTS_CSS = `${ASSET_BASE}/fonts.css`;

// ────────────────────────────────────────────────────────────────────────────
// Service-category icons
//
// Pairs of gold/dark-blue icons (gold = on dark backgrounds; dark = on
// light backgrounds). Mapping is the renderer's best read of the icon set.
// Update if the brand kit ships definitive labels for icons 01/04/07/10.
// ────────────────────────────────────────────────────────────────────────────

interface CategoryIconPair {
  /** Gold variant — use on dark surfaces (dark blue, black, image overlays). */
  goldUrl: string;
  /** Dark-blue variant — use on light surfaces (white, light grey). */
  darkUrl: string;
  /** Short human label for tooltip / alt text. */
  label: string;
}

function pair(goldNumber: string, darkNumber: string, label: string): CategoryIconPair {
  return {
    goldUrl: `${ASSET_BASE}/icons/icon-${goldNumber}.png`,
    darkUrl: `${ASSET_BASE}/icons/icon-${darkNumber}.png`,
    label,
  };
}

/**
 * Best-effort match a scope name (e.g. "WiFi Extension — Main House") to
 * a `DealCategory` so the magazine renderer can pick the right icon.
 *
 * Pure keyword scan — case-insensitive, falls back to null when no
 * keyword hits. Renderer should treat null as "no icon for this scope".
 */
export function inferDealCategoryFromScopeName(name: string): DealCategory | null {
  const n = name.toLowerCase();
  if (/(starlink|sat dish|satellite)/.test(n)) return DealCategory.STARLINK;
  if (/(nbn|fibre|fixed wireless|broadband|adsl)/.test(n)) return DealCategory.NBN;
  if (/(wifi|wi-fi|access point|mesh|network)/.test(n)) return DealCategory.WIFI_EXTENSION;
  if (/(smart home|automation|smart light|smart lock)/.test(n)) return DealCategory.SMART_HOME;
  if (/(access control|door strike|mag ?lock|electric strike|rfid|request to exit|\brex\b|door release)/.test(n)) return DealCategory.ACCESS_CONTROL;
  if (/(\bav\b|audio ?visual|home ?theatre|home ?theater|soundbar|amplifier|projector|tv mount|multi-?room audio)/.test(n)) return DealCategory.AV;
  if (/(security|surveillance|cctv|camera|alarm|intercom)/.test(n)) return DealCategory.SECURITY;
  if (/(voip|sip|phone|handset)/.test(n)) return DealCategory.VOIP;
  if (/(server|firewall|workstation|rack|it )/.test(n)) return DealCategory.IT;
  return null;
}

export const TECHERY_CATEGORY_ICONS: Record<DealCategory, CategoryIconPair> = {
  WIFI_EXTENSION: pair('02', '08', 'Network mesh'),
  SMART_HOME: pair('03', '09', 'Connected home'),
  STARLINK: pair('05', '11', 'Satellite'),
  NBN: pair('06', '12', 'Wireless signal'),
  // Tentative — confirm when icon set labels are finalised.
  VOIP: pair('01', '07', 'Wireless growth'),
  IT: pair('04', '10', 'Service ring'),
  // No clear icon match — magazine renderer falls back to a Lucide
  // shield/lock icon for SECURITY (handled by the section component).
  SECURITY: pair('04', '10', 'Service ring'),
  // No dedicated brand icons for these — renderer falls back to a Lucide
  // key/receipt icon (handled by the section component).
  ACCESS_CONTROL: pair('04', '10', 'Service ring'),
  AV: pair('04', '10', 'Service ring'),
  FEE_FOR_SERVICE: pair('04', '10', 'Service ring'),
};

// ────────────────────────────────────────────────────────────────────────────
// Brand copy
// ────────────────────────────────────────────────────────────────────────────

export const TECHERY_TAGLINE = 'The Techery Southern Highlands';

export const TECHERY_PLACEHOLDER_INTRO = `
We work with homes and businesses across the Southern Highlands to design,
install, and look after the technology that keeps you connected — internet,
wireless, voice, security, and smart-home gear. We treat every install as if
we lived in the house ourselves: clean cabling, tidy mounts, no surprises.

Have a read of what we've put together for you. If anything looks off, drop
us a line — and when you're happy, tap Accept at the bottom of the page.
`.trim();

// ────────────────────────────────────────────────────────────────────────────
// Theme tokens (consumed by MagazineRenderer + paletteFor())
// ────────────────────────────────────────────────────────────────────────────
//
// Colour usage (per brand guide §17):
//   - Dark blue + light grey + white = primary "block" colours
//   - Gold = accent, sparingly
//
// Layout scale: per-brand-guide type hierarchy (54/63 headings, 23/32
// breakouts, 10/16 body) translated to web-friendly defaults. Magazine
// renderer scales these down for body text and up for hero.
// ────────────────────────────────────────────────────────────────────────────

export const TECHERY_THEME_TOKENS: ProposalThemeTokens = {
  // Light palette
  primary: TECHERY_COLOURS.darkBlue,
  secondary: TECHERY_COLOURS.gold,
  accent: TECHERY_COLOURS.darkBlue,
  textPrimary: TECHERY_COLOURS.darkBlue,
  textSecondary: '#3a4258',  // softened dark blue for body
  textMuted: '#7d8497',
  surface: TECHERY_COLOURS.white,
  surfaceMuted: TECHERY_COLOURS.lightGrey,
  border: '#dcdee2',

  // Dark palette — proposal viewer's dark-mode toggle. Brandmark-on-
  // dark-blue artwork already exists, so we lean into the navy as the
  // dark surface rather than going pure black.
  primaryDark: TECHERY_COLOURS.gold,
  secondaryDark: TECHERY_COLOURS.gold,
  textPrimaryDark: TECHERY_COLOURS.white,
  textSecondaryDark: '#c8cdd9',
  textMutedDark: '#8b91a3',
  surfaceDark: TECHERY_COLOURS.darkBlue,
  surfaceMutedDark: '#172037',  // slightly lighter than brand dark blue
  borderDark: '#2a3552',

  // Typography — Poppins is loaded via /branding/techery-au/fonts.css
  // for the client-side viewer, and via Google Fonts URL for PDF render
  // (see renderToHtml.ts GOOGLE_FONT_URLS map).
  fontHeading: 'Poppins',
  fontBody: 'Poppins',

  // Layout
  heroHeightVh: 88,
  sectionSpacingPx: 96,
  galleryColumns: 3,
  contentMaxWidthPx: 1080,

  // Motion
  enableScrollReveals: true,
  enableHeroParallax: true,
  enableLightbox: true,
};

// ────────────────────────────────────────────────────────────────────────────
// Convenience export — the full branding bundle, consumed via
// `getTenantBranding(tenantId)` from `./index.ts`.
// ────────────────────────────────────────────────────────────────────────────

export const techeryAuBranding = {
  tenantId: 'techery-au',
  themeTokens: TECHERY_THEME_TOKENS,
  logos: TECHERY_LOGOS,
  fontsCssUrl: TECHERY_FONTS_CSS,
  categoryIcons: TECHERY_CATEGORY_ICONS,
  tagline: TECHERY_TAGLINE,
  placeholderIntro: TECHERY_PLACEHOLDER_INTRO,
} as const;

export type TenantBranding = typeof techeryAuBranding;
